// angular imports
import { Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';

// devextreme imports
import { DxDataGridComponent, DxPopupComponent } from 'devextreme-angular';

// cc-common import
import { MessageBoxComponent, Utils } from 'cc-common';

// component imports
import { WorkOrderBaseComponent } from '../../_base/work-order-base-component';

// model imports
import { BasePropertyParametersDto } from '../../_models/base-property-parameter-dto';
import { WorkOrderVendorListResultDto } from '../../_models/work-order-vendor-list-result-dto';
import { KeyValueResultDto } from '../../../_models/key-value-result-dto';
import { BaseWorkOrderParameterDto } from '../../_models/base-work-order-parameter-dto';
import { WorkOrderTemporaryVendorsResultDto } from '../../_models/work-order-temporary-vendors-result-dto';

// constant imports
import { VendorConstants } from '../../_constants/vendor-constants';

@Component({
    selector: 'app-select-vendor-for-proposal',
    templateUrl: './select-vendor-for-proposal.component.html',
    styleUrls: ['./select-vendor-for-proposal.component.scss']
})
export class SelectVendorForProposalComponent extends WorkOrderBaseComponent implements OnInit {
    @ViewChild('selectVendorProposalPopup', { static: false }) selectVendorProposalPopup: DxPopupComponent;
    @ViewChild('vendorDataGrid', { static: false }) vendorDataGrid: DxDataGridComponent;
    @ViewChild('temporaryVendorDataGrid', { static: false }) temporaryVendorDataGrid: DxDataGridComponent;
    @ViewChild('venodorForProposalMessageBox', { static: false }) venodorForProposalMessageBox: MessageBoxComponent;

    @Input() communityId: number;
    @Input() workOrderId: number;
    @Input() selection: string;

    @Output() onVendorSelection = new EventEmitter<WorkOrderVendorListResultDto[]>();
    @Output() onClose = new EventEmitter<void>();

    vendorList: Array<WorkOrderVendorListResultDto> = [];
    temporaryVendorList: Array<WorkOrderTemporaryVendorsResultDto> = [];
    locationVendorList: Array<WorkOrderVendorListResultDto> = [];

    vendorTypes: Array<KeyValueResultDto> = [];
    selectedVendorType: string;

    showAddNewTemporaryVendor = false;

    visible = false;

    constructor(protected readonly injector: Injector) {
        super(injector);
    }

    ngOnInit() {
        this.vendorTypes = [
            {
                key: VendorConstants.COMMUNITY_VENDOR,
                displayText: 'Community',
                isDefault: true
            },
            {
                key: VendorConstants.ALL_VENDOR,
                displayText: 'All',
                isDefault: false
            },
            {
                key: VendorConstants.TEMP_VENDOR,
                displayText: 'Temporary',
                isDefault: false
            }
        ];
        this.selectedVendorType = VendorConstants.COMMUNITY_VENDOR;

        this.show();
    }

    show(): void {
        this.visible = true;
        this.loadVendorList();
    }

    onPopupShown(): void {
        this.selectVendorProposalPopup.instance.repaint();
    }

    onPopupClose(): void {
        this.closeAddVendorProposal();
    }

    onVendorTypeChange(event: any): void {
        this.loadVendorList();
    }

    onMultipleSelectionClick(): void {
        let selectedRowsData: Array<any> = [];
        if (Utils.equalsIgnoreCase(this.selectedVendorType, VendorConstants.TEMP_VENDOR)) {
            selectedRowsData = this.temporaryVendorDataGrid.instance.getSelectedRowsData();
            selectedRowsData.forEach((row) => {
                row.proposalVendorType = VendorConstants.TEMP_VENDOR;
            });
        } else {
            selectedRowsData = this.vendorDataGrid.instance.getSelectedRowsData();
            selectedRowsData.forEach((row) => {
                row.proposalVendorType = Utils.equalsIgnoreCase(
                    this.selectedVendorType,
                    VendorConstants.COMMUNITY_VENDOR
                )
                    ? VendorConstants.COMMUNITY_VENDOR
                    : VendorConstants.ALL_VENDOR;
            });
        }
        if (Utils.notNullOrEmpty(selectedRowsData)) {
            this.onVendorSelection.emit(selectedRowsData);
            this.closeAddVendorProposal();
        } else {
            this.venodorForProposalMessageBox.showErrorMessage('Warning', 'Please select vendors.');
        }
    }

    onAddNewTemporaryVendorClick(): void {
        this.showAddNewTemporaryVendor = true;
    }

    onAddNewTemporaryVendorClose(refresh: boolean): void {
        this.showAddNewTemporaryVendor = false;
        if (Utils.isTrue(refresh)) {
            this.loadVendorList();
        }
    }

    onSingleSelection(record: any): void {
        if (Utils.equalsIgnoreCase(this.selectedVendorType, VendorConstants.TEMP_VENDOR)) {
            record.proposalVendorType = VendorConstants.TEMP_VENDOR;
        } else {
            record.proposalVendorType = Utils.equalsIgnoreCase(
                this.selectedVendorType,
                VendorConstants.COMMUNITY_VENDOR
            )
                ? VendorConstants.COMMUNITY_VENDOR
                : VendorConstants.ALL_VENDOR;
        }
        this.onVendorSelection.emit([record]);
        this.closeAddVendorProposal();
    }

    private populateCommunityVendors(clearCache = false): void {
        const baseParams: BasePropertyParametersDto = new BasePropertyParametersDto();
        baseParams.communityId = this.communityId;
        baseParams.clearCache = clearCache;

        this.workOrderHelperService.showLoading();

        this.workOrderService.getCommunityVendors(baseParams).subscribe(
            (result: WorkOrderVendorListResultDto[]) => {
                this.vendorList = result;
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private populateTemporaryVendors(): void {
        const baseParams: BaseWorkOrderParameterDto = new BaseWorkOrderParameterDto();
        baseParams.communityId = this.communityId;
        baseParams.workOrderId = this.workOrderId;

        this.workOrderHelperService.showLoading();
        this.workOrderService.getTemporaryVendors(baseParams).subscribe(
            (result: WorkOrderTemporaryVendorsResultDto[]) => {
                this.temporaryVendorList = result;
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private populateLocationVendors(clearCache = false): void {
        const baseParams: BasePropertyParametersDto = new BasePropertyParametersDto();
        baseParams.communityId = this.communityId;
        baseParams.clearCache = clearCache;

        this.workOrderHelperService.showLoading();

        this.workOrderService.getLocationVendors(baseParams).subscribe(
            (result: WorkOrderVendorListResultDto[]) => {
                this.vendorList = result;
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private loadVendorList(): void {
        if (Utils.equalsIgnoreCase(this.selectedVendorType, VendorConstants.COMMUNITY_VENDOR)) {
            this.populateCommunityVendors();
        } else if (Utils.equalsIgnoreCase(this.selectedVendorType, VendorConstants.TEMP_VENDOR)) {
            this.populateTemporaryVendors();
        } else {
            this.populateLocationVendors();
        }
    }

    private closeAddVendorProposal(): void {
        this.visible = false;
        this.onClose.emit();
    }
}
